import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import CoverImage from "./CoverImage";

const TYPE_LABEL = { book: "Book", film: "Film", tv: "Series", youtube: "Video", essay: "Essay" };
const SPEED = 26; // px per second — a slow drift, not a slideshow

// Filmstrip: one hairline-divided track that drifts continuously and loops
// seamlessly (the list is rendered twice and the scroll position wraps at the
// halfway mark). Hovering a pick pauses the drift; leaving resumes it.
export default function PicksCarousel({ picks }) {
  const items = picks || [];
  const n = items.length;
  const track = useRef(null);
  const paused = useRef(false);
  const offset = useRef(0);
  const reduce = useReducedMotion();
  const loop = n > 1 ? [...items, ...items] : items;

  useEffect(() => {
    const el = track.current;
    if (!el || n < 2 || reduce) return undefined;
    let frame;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(now - last, 100) / 1000;
      last = now;
      const half = el.scrollWidth / 2;
      if (!paused.current && half > 0) {
        offset.current += SPEED * dt;
        if (offset.current >= half) offset.current -= half;
        el.scrollLeft = offset.current;
      } else {
        // keep the accumulator in step with manual scrolling / arrow nudges
        offset.current = el.scrollLeft;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [n, reduce]);

  const nudge = (dir) => {
    const el = track.current;
    if (!el) return;
    const step = el.firstElementChild?.offsetWidth || 320;
    const half = el.scrollWidth / 2;
    let next = el.scrollLeft + dir * step;
    if (next < 0) next += half;
    if (next >= half) next -= half;
    offset.current = next;
    el.scrollTo({ left: next, behavior: reduce ? "auto" : "smooth" });
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); nudge(1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); nudge(-1); }
  };

  if (!n) return null;

  return (
    <div
      className="relative border-y border-line"
      data-testid="picks-carousel"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
      onFocusCapture={() => { paused.current = true; }}
      onBlurCapture={() => { paused.current = false; }}
    >
      <div
        ref={track}
        onKeyDown={onKeyDown}
        data-lenis-prevent
        tabIndex={0}
        role="region"
        aria-label="Picks of the month"
        data-testid="picks-track"
        className="flex overflow-x-auto no-scrollbar"
      >
        {loop.map((p, idx) => (
          <article
            key={`${p.id || "pick"}-${idx}`}
            data-testid={`pick-${idx}`}
            aria-hidden={idx >= n ? "true" : undefined}
            className="group shrink-0 w-[88vw] sm:w-[62vw] lg:w-[46vw] xl:w-[38vw] border-r border-line px-6 py-8 lg:px-10 lg:py-12 flex flex-col sm:flex-row gap-5 sm:gap-6 lg:gap-9"
          >
            <Link
              to={`/library/${p.id}`}
              tabIndex={-1}
              aria-hidden="true"
              className="relative self-start w-28 sm:w-32 lg:w-40 shrink-0 aspect-[2/3] overflow-hidden border border-line"
            >
              <CoverImage src={p.cover_url} alt={p.title} focal={p.cover_focal} size="detail" title={p.title} creator={p.creator} year={p.year} />
            </Link>
            <div className="min-w-0 flex flex-col">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crimson mb-3">
                {TYPE_LABEL[p.media_type] || p.media_type}
              </p>
              <h3 className="font-serif text-2xl lg:text-4xl font-light leading-[1.05] tracking-tight text-vellum">
                <Link
                  to={`/library/${p.id}`}
                  data-testid={`pick-link-${idx}`}
                  tabIndex={idx >= n ? -1 : undefined}
                  className="hover:text-brass transition-colors"
                >
                  {p.title}
                </Link>
              </h3>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-vellum-muted mt-3">
                {p.creator}{p.year ? ` · ${p.year}` : ""}
              </p>
              {p.note && (
                <p className="mt-5 text-sm text-vellum-muted italic leading-relaxed line-clamp-5">{p.note}</p>
              )}
              <Link
                to={`/library/${p.id}`}
                data-testid={`pick-explore-${idx}`}
                tabIndex={idx >= n ? -1 : undefined}
                className="mt-auto pt-6 self-start inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-vellum-muted group-hover:text-crimson transition-colors"
              >
                Explore <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </article>
        ))}
      </div>

      {n > 1 && (
        <div className="flex items-stretch justify-between border-t border-line">
          <p className="px-6 lg:px-10 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-vellum-muted" data-testid="picks-hint">
            {n} picks <span className="hidden sm:inline text-vellum-faint">· hover to pause</span>
          </p>
          <div className="flex divide-x divide-line border-l border-line">
            <button
              onClick={() => nudge(-1)}
              data-testid="picks-prev"
              aria-label="Previous pick"
              className="px-6 text-vellum-muted hover:text-crimson hover:bg-ink-800 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => nudge(1)}
              data-testid="picks-next"
              aria-label="Next pick"
              className="px-6 text-vellum-muted hover:text-crimson hover:bg-ink-800 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
